import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: this is a daylight scene lit by one sun that the track
// itself moves. A club rig inside it would have nothing to stand on.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js). requiresFloatTextures
// is documentation: what actually keeps this out of the rotation on a device
// without renderable float targets is `enabled`, which the registry reads at
// import time - the same contract silk and swarm use.
export const PROFILE = {
  id: 'ocean',
  family: 'photographic',
  danceability: 0.12,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.15,
  bpmMin: 40,
  bpmMax: 130,
  movement: 0.20,
  tonality: 0.92,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.40] },
  enabled: FLOAT_TEXTURES_OK
};

// Ocean: a real sea surface by spectral synthesis - Tessendorf's method, the
// one every ocean in cinema has used since the nineties. A Phillips spectrum
// says how much energy sits at each wavelength and how strongly it aligns with
// the wind; an inverse FFT turns that into a height field. The result has
// crests that sharpen and troughs that flatten, which a sum of sines never
// produces however many terms you give it.
//
// FFT, NOT THE GERSTNER FALLBACK. The brief offered 64 summed Gerstner waves
// as a way out. The FFT is what makes the crests behave, so it is what is
// implemented here - and one packing decision makes it affordable:
//
//   THREE REAL FIELDS IN ONE COMPLEX PAIR. The brief budgets 48 passes: three
//   fields (height, Dx, Dz) x 16 passes of a 256 transform. But an RGBA32F
//   texel carries TWO complex numbers, and the butterfly treats RG and BA
//   independently, so one run of passes transforms two complex fields at once.
//   Better still, the spectra here are Hermitian (they have to be - the fields
//   they produce are real), and the inverse transform of A + i*B is then
//   a + i*b with a and b BOTH real. So Ĥ + i*D̂x rides in RG and comes out as
//   height in R and Dx in G. Three real fields therefore cost ONE run:
//   16 passes at 256, 14 at 128, against the brief's 48.
//
// MANUAL BILINEAR, EVERYWHERE THE FIELD IS READ. The Pi's V3D 4.2 reports
// EXT_color_buffer_float but NOT OES_texture_float_linear (measured, not
// assumed): float textures there can only be sampled NEAREST. Since the
// projected grid samples the height field at positions that never line up with
// texels, hardware NEAREST would print the FFT grid as visible steps across
// the whole sea. Every read below is a four-tap bilinear done by hand.
//
// THE SUN CROSSES THE TRACK. Sun elevation is driven by progress: high and
// pale at the start, descending through orange to red, touching the horizon as
// the track ends. That is the piece's structure - every track is one complete
// sunset. On a web radio with no duration, the same synthetic progress the
// director uses stands in.

const TIER = {
  // Tier 0 is unavailable by contract (requiresTier: 1) - there is no version
  // of this that is worth running below a 128 transform.
  // cloudOctaves shapes the SKY; reflectOctaves shapes the sky the water
  // reflects, and 0 there is the brief's "ciel simplifie" for tier 1.
  1: { n: 128, grid: 128, cloudOctaves: 2, reflectOctaves: 0 },
  2: { n: 256, grid: 192, cloudOctaves: 4, reflectOctaves: 3 }
};
const DEFAULT_TIER = 2;

const PATCH_SIZE = 180.0;        // world units covered by one FFT patch
const GRAVITY = 9.81;
const CAMERA_HEIGHT = 9.0;
const CAMERA_PITCH = 0.083;      // tan of the downward tilt; see _placeCamera
const FOV = 55;

// Wind speed sets the dominant wavelength through Phillips' L = V^2/g, which
// is the scale BELOW which the spectrum is cut. At 26 that came to L = 69 on a
// 180-unit patch, so the cut never engaged, the raw 1/k^4 took over and the
// whole sea collapsed onto the single longest wave the patch can hold - one
// slow tilt with ripples on it, which is what the height debug showed. At 9 the
// cut lands near a 50-unit wavelength and the patch carries real swell.
const WIND_BASE = 9.0;
const WIND_BAND = 0.22;          // bands.mid, +-22%
const AMPLITUDE_BASE = 0.00042;
const AMPLITUDE_BAND = 0.18;     // bands.low, +-18% - the sea deepens on bass
// Horizontal displacement. Past about 0.6 the jacobian folds so hard that the
// crests stop sharpening and start growing spikes - the surface is passing
// through itself. This is the knob ?oceanchop=0 turns off.
const CHOPPY = 0.95;
const FOAM_DECAY = 0.985;
const FOAM_THRESHOLD = 0.72;     // jacobian below this is a breaking crest
const SAFE_FLOOR = 0.55;         // a natural scene; a dark patch would read as dirt
// The frame's top edge sits about 32 degrees up, so a sun starting at 36 was
// simply never in shot. 0.42 puts it in the upper sky at the start of a track
// and on the horizon at the end.
const SUN_ELEVATION_START = 0.42;
const SUN_AZIMUTH = 0.16;
// The height the sea is normalised TO, in world units, at the base amplitude.
// Everything about the spectrum's shape is free to change with the wind; its
// overall size is pinned here so the music can move the waves' character
// without the sea growing or collapsing as a side effect.
const CUTOFF_TEXELS = 3.0;
const TARGET_RMS = 1.15;
const HUE_TARGET = 0.515;        // blue-green
const HUE_WINDOW = 0.065;        // how far the artwork may lean it, either way
// THE SEA DOES NOT KEEP TIME. The bands arrive pulsing at the beat, and fed
// straight into the wave field they pumped its height by 19.2% of its mean
// with 61 direction reversals in three seconds - a ~10Hz shiver on top of a
// 2Hz beat, which is the trembling. Smoothed over BAND_TAU the sea answers the
// SECTION instead: still music-driven, no longer beat-driven. The beat now
// lives on the cover's halo, where a pulse belongs.
const BAND_TAU = 5.0;            // seconds, for the wave field
const SPARKLE_TAU = 1.6;         // shorter: light may breathe, geometry may not
const HALO_BASE = 0.30;          // the halo is there even between beats
const HALO_PULSE = 1.5;          // how far director.pulse lifts it on the beat
const HALO_DECAY = 5.5;
const HALO_RING = 1.22;          // peak, in cover half-sizes: just outside it
const HALO_WIDTH = 0.62;
const RADIO_LOOP_SECONDS = 210;  // matches director.js's syntheticProgress

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// ---------------------------------------------------------------------------
// Shared GLSL

// Four-tap bilinear on a NEAREST float texture. See the header: the Pi cannot
// filter these in hardware. uTexel is 1/N.
const BILINEAR_CHUNK = `
  vec4 texBilinear(sampler2D tex, vec2 uv, float n) {
    vec2 st = uv * n - 0.5;
    vec2 fl = floor(st);
    vec2 fr = st - fl;
    vec2 base = (fl + 0.5) / n;
    float d = 1.0 / n;
    vec4 a = texture2D(tex, base);
    vec4 b = texture2D(tex, base + vec2(d, 0.0));
    vec4 c = texture2D(tex, base + vec2(0.0, d));
    vec4 e = texture2D(tex, base + vec2(d, d));
    return mix(mix(a, b, fr.x), mix(c, e, fr.x), fr.y);
  }
`;

const QUAD_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// Same quad, but parked on the far plane so the depth test can reject it where
// the water already covers it - see where the sky mesh is built.
const SKY_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 1.0, 1.0);
  }
`;

// --- H0: the initial spectrum, generated once (and again only when the wind
// has moved enough to matter). The gaussians are a pure function of the texel
// index, so regenerating does NOT reshuffle the sea - the same wave stays the
// same wave and only the spectrum's envelope changes, which is what lets the
// wind respond to the music without the surface ever jumping.
const H0_FRAGMENT = `
  precision highp float;
  uniform float uN;
  uniform float uPatch;
  uniform float uWindSpeed;
  uniform vec2 uWindDir;
  uniform float uAmplitude;
  varying vec2 vUv;

  float hash1(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  // Box-Muller from two uniform hashes.
  vec2 gauss(vec2 idx, float salt) {
    float u1 = max(hash1(idx + vec2(salt, salt * 1.7)), 1e-6);
    float u2 = hash1(idx * 1.61 + vec2(salt * 3.1, salt));
    float r = sqrt(-2.0 * log(u1));
    float a = 6.28318530718 * u2;
    return vec2(r * cos(a), r * sin(a));
  }

  float phillips(vec2 k) {
    float kk = dot(k, k);
    if (kk < 1e-6) return 0.0;
    float L = uWindSpeed * uWindSpeed / ${GRAVITY.toFixed(2)};
    float kh = dot(normalize(k), uWindDir);
    float p = uAmplitude * exp(-1.0 / (kk * L * L)) / (kk * kk);
    // Alignment with the wind. WITHOUT this the sea is isotropic and reads as
    // fake immediately - it is the single term that makes waves come from a
    // direction instead of from everywhere.
    p *= kh * kh;
    // SHORT-WAVE CUT, expressed in TEXELS rather than as the brief's fixed
    // exp(-kk * 0.5). That constant belongs to a metre-scale patch; here the
    // patch is 180 units across 256 texels, so it left full-strength energy
    // right down to the texel spacing - and since the near sea is read by
    // bilinear interpolation of that grid, every texel became a visible tent.
    // The whole foreground was a field of triangular teeth, with or without
    // choppiness. Cutting below CUTOFF_TEXELS texels makes the field smooth at
    // the scale the interpolation works on, and scales correctly with tier.
    float lmin = ${CUTOFF_TEXELS.toFixed(1)} * uPatch / uN;
    p *= exp(-kk * lmin * lmin * 0.25);
    return p;
  }

  vec2 waveVector(vec2 idx) {
    return 6.28318530718 * (idx - uN * 0.5) / uPatch;
  }

  void main() {
    vec2 idx = floor(vUv * uN);
    vec2 k = waveVector(idx);
    vec2 g = gauss(idx, 1.0);
    vec2 h0 = g * sqrt(max(phillips(k), 0.0) * 0.5);

    // conj(H0(-k)). -k is the mirrored texel, and because the gaussians are a
    // pure function of the index we can evaluate it here instead of needing a
    // second pass or a readback.
    vec2 midx = uN - idx;
    vec2 mk = waveVector(midx);
    vec2 mg = gauss(midx, 1.0);
    vec2 h0m = mg * sqrt(max(phillips(mk), 0.0) * 0.5);
    vec2 h0mConj = vec2(h0m.x, -h0m.y);

    gl_FragColor = vec4(h0, h0mConj);
  }
`;

// --- The per-frame spectrum: H(k,t) plus the two horizontal displacement
// spectra, packed for a single FFT run.
const SPECTRUM_FRAGMENT = `
  precision highp float;
  uniform sampler2D uH0;
  uniform float uN;
  uniform float uPatch;
  uniform float uTime;
  uniform float uChoppy;
  varying vec2 vUv;

  vec2 cmul(vec2 a, vec2 b) {
    return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
  }

  void main() {
    vec2 idx = floor(vUv * uN);
    vec2 k = 6.28318530718 * (idx - uN * 0.5) / uPatch;
    float klen = length(k);

    vec4 h0 = texture2D(uH0, vUv);

    // Deep-water dispersion. This is what makes long waves outrun short ones,
    // and it is the whole reason the swell reads as swell rather than as a
    // texture scrolling past.
    float w = sqrt(${GRAVITY.toFixed(2)} * klen);
    float c = cos(w * uTime);
    float s = sin(w * uTime);
    vec2 H = cmul(h0.xy, vec2(c, s)) + cmul(h0.zw, vec2(c, -s));

    // Horizontal displacement: Dx = i*kx/|k| * H, Dz = i*kz/|k| * H. Without
    // these the crests stay rounded; with them they pinch into ridges, which
    // is the signature of a real sea.
    vec2 kn = klen > 1e-6 ? k / klen : vec2(0.0);
    vec2 Dx = cmul(vec2(0.0, kn.x * uChoppy), H);
    vec2 Dz = cmul(vec2(0.0, kn.y * uChoppy), H);

    // Pack: RG carries H + i*Dx (one inverse transform yields both as the real
    // and imaginary halves), BA carries Dz. See the header.
    vec2 packA = vec2(H.x - Dx.y, H.y + Dx.x);
    vec2 packB = Dz;

    // The centred layout is undone on the OUTPUT side, in the last butterfly
    // pass - see uUnshift there for why it cannot be done here.
    gl_FragColor = vec4(packA, packB);
  }
`;

// --- One Stockham butterfly stage. ONE program for every pass: the stage size
// and the axis are uniforms, so the Pi's driver compiles this once rather than
// sixteen times.
const FFT_FRAGMENT = `
  precision highp float;
  uniform sampler2D uSrc;
  uniform float uN;
  uniform float uSubSize;     // 2, 4, 8, ... N
  uniform float uHorizontal;  // 1.0 = transform along x
  uniform float uUnshift;     // 1.0 on the LAST pass only
  varying vec2 vUv;

  vec2 cmul(vec2 a, vec2 b) {
    return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
  }

  void main() {
    vec2 uv = vUv;
    float index = (uHorizontal > 0.5 ? uv.x : uv.y) * uN - 0.5;

    float half_ = uSubSize * 0.5;
    float evenIndex = floor(index / uSubSize) * half_ + mod(index, half_);

    vec2 uvEven = uHorizontal > 0.5
      ? vec2((evenIndex + 0.5) / uN, uv.y)
      : vec2(uv.x, (evenIndex + 0.5) / uN);
    vec2 uvOdd = uHorizontal > 0.5
      ? vec2((evenIndex + uN * 0.5 + 0.5) / uN, uv.y)
      : vec2(uv.x, (evenIndex + uN * 0.5 + 0.5) / uN);

    vec4 even = texture2D(uSrc, uvEven);
    vec4 odd = texture2D(uSrc, uvOdd);

    // INVERSE transform: +2*pi. The 1/N^2 that belongs with it is folded into
    // the amplitude further up rather than spent as a pass of its own.
    float arg = 6.28318530718 * (index / uSubSize);
    vec2 tw = vec2(cos(arg), sin(arg));

    vec4 res = vec4(even.xy + cmul(tw, odd.xy),
                    even.zw + cmul(tw, odd.zw));

    // UNDO THE CENTRED LAYOUT. The spectrum is stored with k=0 in the middle,
    // which is the standard layout shifted by N/2 - and by the shift theorem
    // the transform of a shifted spectrum is the true field multiplied by
    // (-1)^(x+z). Left in, that is a checkerboard at texel scale, and the
    // bilinear read of it turned the entire sea into a regular lattice of
    // little pyramids. Premultiplying the INPUT instead does NOT fix it: that
    // shifts the output by half a patch and leaves the sign exactly where it
    // was. It has to be undone here, on the way out.
    if (uUnshift > 0.5) {
      vec2 xy = floor(gl_FragCoord.xy);
      res *= mod(xy.x + xy.y, 2.0) < 0.5 ? 1.0 : -1.0;
    }
    gl_FragColor = res;
  }
`;

// --- Normals, and the foam the jacobian says is breaking.
const NORMFOAM_FRAGMENT = `
  precision highp float;
  uniform sampler2D uDisp;
  uniform sampler2D uPrev;
  uniform float uN;
  uniform float uPatch;
  uniform float uScale;
  uniform float uFoamDecay;
  uniform float uFoamThreshold;
  uniform float uReset;
  varying vec2 vUv;

  void main() {
    float d = 1.0 / uN;
    float step = uPatch / uN;

    vec4 c = texture2D(uDisp, vUv) * uScale;
    vec4 xp = texture2D(uDisp, vUv + vec2(d, 0.0)) * uScale;
    vec4 xm = texture2D(uDisp, vUv - vec2(d, 0.0)) * uScale;
    vec4 zp = texture2D(uDisp, vUv + vec2(0.0, d)) * uScale;
    vec4 zm = texture2D(uDisp, vUv - vec2(0.0, d)) * uScale;

    float dhdx = (xp.x - xm.x) / (2.0 * step);
    float dhdz = (zp.x - zm.x) / (2.0 * step);

    // Jacobian of the horizontal displacement. Where it goes negative the
    // surface has folded over itself - that is a wave breaking, and it is the
    // physically right place to put foam rather than a height threshold.
    float dDxdx = (xp.y - xm.y) / (2.0 * step);
    float dDzdz = (zp.z - zm.z) / (2.0 * step);
    float dDxdz = (zp.y - zm.y) / (2.0 * step);
    float dDzdx = (xp.z - xm.z) / (2.0 * step);
    float jac = (1.0 + dDxdx) * (1.0 + dDzdz) - dDxdz * dDzdx;

    // THE SLOPES HAVE TO KNOW ABOUT THE HORIZONTAL DISPLACEMENT. The surface is
    // x' = x + Dx, so the slope the eye sees is dh/dx' = (dh/dx)/(1 + dDx/dx),
    // not dh/dx. Computed from the height alone, the normals were identical
    // with choppiness on and off - the crests moved but the LIGHTING never
    // showed it, which is most of what the displacement is there to buy.
    dhdx /= max(1.0 + dDxdx, 0.18);
    dhdz /= max(1.0 + dDzdz, 0.18);

    float born = smoothstep(uFoamThreshold, uFoamThreshold - 0.55, jac);
    float prev = texture2D(uPrev, vUv).b * uFoamDecay * uReset;
    float foam = clamp(max(prev, born), 0.0, 1.0);

    gl_FragColor = vec4(dhdx, dhdz, foam, jac);
  }
`;

// ---------------------------------------------------------------------------
// The sky. One function, called twice: once for the background and once with
// the reflected vector from the water, so the sea can only ever reflect the
// sky that is actually there.
// octaves = 0 emits a cloudless sky: the gradient and the sun, nothing else.
// That is the version the WATER reflects at tier 1 - running the cloud fbm once
// per sea pixel was the single most expensive thing in the frame, and what it
// buys is detail inside a reflection that the Fresnel term has already dimmed.
function skyChunk(octaves) {
  let fbm = '';
  let amp = 0.55, freq = 1.0;
  for (let i = 0; i < octaves; i++) {
    fbm += `    v += ${amp.toFixed(4)} * vnoise2(p * ${freq.toFixed(3)});\n`;
    amp *= 0.5; freq *= 2.17;
  }
  const clouds = octaves > 0 ? `
    // Cloud bands, stretched hard along x so they read as the long streaks a
    // low sun lights from underneath rather than as blobs.
    float above = smoothstep(-0.02, 0.10, h);
    if (above > 0.001) {
      vec2 cuv = dir.xz / max(h, 0.06);
      cuv = vec2(cuv.x * 0.16 + uCloudDrift, cuv.y * 0.85);
      float band = cloudFbm(cuv);
      band = smoothstep(0.46, 0.80, band) * above;
      float under = 1.0 - smoothstep(0.0, 0.45, uSunElevation);
      float rim = pow(max(cd, 0.0), 5.0);
      vec3 cloudCol = mix(vec3(0.62, 0.65, 0.70), uSunColor * 1.25, under * (0.35 + 0.65 * rim));
      col = mix(col, cloudCol, band * 0.80);
    }` : '';
  return `
  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform vec3 uZenithColor;
  uniform vec3 uHorizonColor;
  uniform float uSunElevation;
  uniform float uCloudDrift;

  float hash2(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  float vnoise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
               mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  float cloudFbm(vec2 p) {
    float v = 0.0;
${fbm}
    return v;
  }

  vec3 skyColor(vec3 dir) {
    float h = clamp(dir.y, -1.0, 1.0);
    float up = pow(clamp(h, 0.0, 1.0), 0.42);
    vec3 col = mix(uHorizonColor, uZenithColor, up);

    // Sun disk and its halo. The disk is deliberately tiny and very bright:
    // it is what the specular column on the water is a reflection OF.
    float cd = dot(dir, uSunDir);
    float disk = smoothstep(0.99965, 0.99992, cd);
    float halo = pow(max(cd, 0.0), 1400.0) * 0.55 + pow(max(cd, 0.0), 45.0) * 0.08;
    col += uSunColor * (disk * 14.0 + halo);

${clouds}
    return col;
  }
`;
}

// ---------------------------------------------------------------------------
// Water. The projected grid lives in the vertex shader: a mesh that is regular
// on SCREEN and lands on the water plane, so the vertices are where the pixels
// are instead of being wasted at the horizon.
const WATER_VERTEX = `
  precision highp float;
  uniform mat4 uInvViewProj;
  // Shared with the fragment stage, so the precision is stated on BOTH sides:
  // the vertex stage defaults to highp and the fragment stage to mediump, and
  // a uniform whose precision differs between them fails to LINK.
  uniform highp vec3 uCamPos;
  uniform highp sampler2D uDisp;
  uniform highp float uN;
  uniform highp float uPatch;
  uniform highp float uScale;
  uniform float uMarginX;
  uniform float uMarginUp;
  uniform float uMarginDown;
  uniform float uMaxDist;
  uniform float uFadeStart;

  uniform highp float uAspect;
  uniform highp vec2 uSafeCenter;
  uniform highp vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec3 vWorld;
  varying vec2 vField;
  varying float vDist;
  varying float vFade;
  varying float vSafe;
  varying vec2 vNdc;

${BILINEAR_CHUNK}

  void main() {
    // The grid overshoots the frame, and far more BELOW it than above. A
    // vertex here is placed on the flat plane and only then lifted by the wave,
    // so the bottom row rides up the screen with the swell: at the nearest
    // water a three-unit crest swings the view angle by about nine degrees,
    // a third of the half-height, and the old symmetric 1.08 margin let that
    // uncover the edge of the mesh - the hollow arc of sky at the foot of the
    // frame whenever the sea rose. Spread over the wider range rather than
    // scaled, so the vertices stay evenly spaced.
    vec2 ndc = vec2(position.x * uMarginX,
                    mix(-uMarginDown, uMarginUp, position.y * 0.5 + 0.5));

    vec4 pn = uInvViewProj * vec4(ndc, -1.0, 1.0);
    vec4 pf = uInvViewProj * vec4(ndc, 1.0, 1.0);
    vec3 a = pn.xyz / pn.w;
    vec3 b = pf.xyz / pf.w;
    vec3 dir = normalize(b - a);

    // Rays that never come down onto the plane (at or above the horizon) get
    // FLATTENED onto it at maximum range rather than followed: a + dir*uMaxDist
    // for an upward ray lands high in the sky, and those vertices then drew a
    // sheet of water across the top of the frame. Pinning them to y = 0 at the
    // far distance puts them on the horizon line instead, where they belong.
    float denom = dir.y;
    vec3 world;
    if (denom < -1e-4) {
      float t = clamp((0.0 - a.y) / denom, 0.0, uMaxDist);
      world = a + dir * t;
    } else {
      vec2 fwd = normalize(dir.xz);
      world = vec3(a.x + fwd.x * uMaxDist, 0.0, a.z + fwd.y * uMaxDist);
    }

    float dist = length(world.xz - uCamPos.xz);
    // Fade the displacement out with distance. A wave a kilometre away is
    // smaller than a pixel, and keeping it only buys shimmer.
    float fade = 1.0 - smoothstep(uFadeStart, uMaxDist * 0.85, dist);
    fade *= fade;

    vec2 field = world.xz / uPatch;
    vec4 disp = texBilinear(uDisp, field, uN) * uScale;

    world.y += disp.x * fade;
    world.xz += vec2(disp.y, disp.z) * fade;

    vWorld = world;
    vField = field;
    vDist = dist;
    vFade = fade;

    vec4 clip = projectionMatrix * viewMatrix * vec4(world, 1.0);
    gl_Position = clip;

    // CIRCULAR, not the shared chunk's box. The brief warned that on a natural
    // scene any dimming reads as a stain, and the box SDF proved it: its lower
    // edge dips just under the horizon and printed a grey rectangle across the
    // far water, which the horizon haze then made obvious by brightening
    // everything around it. A round, very gradual well has no edge to notice.
    // The (ndc.x * aspect, ndc.y) space is isotropic in screen pixels, so a
    // circle here is a circle on screen.
    vec2 sndc = clip.xy / max(clip.w, 0.0001);
    float rad = max(max(uSafeHalfSize.x, uSafeHalfSize.y), 0.001) * 0.85;
    float e = length(vec2(sndc.x * uAspect, sndc.y) - uSafeCenter) / rad;
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.45, 1.90, e));
    vNdc = sndc;
  }
`;

function waterFragment(octaves) {
  return `
  precision mediump float;
  uniform highp sampler2D uNorm;
  uniform highp sampler2D uDisp;
  uniform highp vec3 uCamPos;
  uniform highp float uN;
  uniform highp float uPatch;
  uniform vec3 uDeepColor;
  uniform vec3 uShallowColor;
  uniform float uSparkle;
  uniform highp float uScale;
  uniform float uHeightDebug;

  varying vec3 vWorld;
  varying vec2 vField;
  varying float vDist;
  varying float vFade;
  varying float vSafe;
  varying vec2 vNdc;

${BILINEAR_CHUNK}
${skyChunk(octaves)}
  uniform vec3 uHaloColor;
  uniform float uHaloStrength;
  // Stated highp on BOTH sides: the water declares these in its vertex stage
  // too, where the default is highp, and a uniform whose precision differs
  // between stages fails to link - silently, as far as the page is concerned.
  uniform highp float uAspect;
  uniform highp vec2 uSafeCenter;
  uniform highp vec2 uSafeHalfSize;

  // A ring of light just OUTSIDE the cover, which is where the beat lives now.
  // uSafeHalfSize spans the whole overlay - cover plus the title and artist
  // under it - while the cover is the square at the TOP of that box, so its
  // half-size is the smaller of the box's two and its centre sits that far
  // below the box's top edge. Peaking outside the sleeve rather than behind it
  // keeps the text on a calm ground while the halo reads around the edge.
  vec3 coverHalo(vec2 ndc) {
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    float side = min(uSafeHalfSize.x, uSafeHalfSize.y);
    vec2 centre = vec2(uSafeCenter.x, uSafeCenter.y + uSafeHalfSize.y - side);
    float e = length(sp - centre) / max(side, 0.001);
    float d = (e - ${HALO_RING.toFixed(2)}) / ${HALO_WIDTH.toFixed(2)};
    return uHaloColor * (exp(-d * d) * uHaloStrength);
  }


  void main() {
    vec4 nf = texBilinear(uNorm, vField, uN);

    if (uHeightDebug > 0.5) {
      float h = texBilinear(uDisp, vField, uN).x * uScale;
      gl_FragColor = vec4(vec3(h * 0.10 + 0.5), 1.0);
      return;
    }

    // Slopes come back in world units; the flattening with distance has to
    // match the vertex fade or the far water lights differently from its shape.
    vec3 N = normalize(vec3(-nf.x * vFade, 1.0, -nf.y * vFade));
    vec3 V = normalize(uCamPos - vWorld);
    vec3 R = reflect(-V, N);
    R.y = abs(R.y);          // never sample the sky below the horizon

    // 1. FRESNEL. Grazing angles at the horizon reflect nearly everything,
    // steep angles almost nothing. This term carries half the image: without
    // it the sea reads as painted plastic.
    float f = pow(1.0 - max(dot(N, V), 0.0), 5.0);
    f = mix(0.02, 1.0, f);

    vec3 sky = skyColor(R);

    // 2. THE WATER'S OWN COLOUR: deep in the troughs, lighter on the crests
    // where light has scattered up through a thinner wall of water.
    // Subsurface lift, kept to the faces actually turned towards the sun: as a
    // near-constant it simply raised the whole sea towards the pale colour and
    // no trough ever read as deep.
    float sss = clamp(0.5 + 0.5 * dot(N, normalize(vec3(uSunDir.x, 0.35, uSunDir.z))), 0.0, 1.0);
    vec3 body = mix(uDeepColor, uShallowColor, sss * sss * sss);

    vec3 col = mix(body, sky, f);

    // 3. THE SUN'S GLITTER PATH. A very tight specular lobe; the column under
    // the sun builds itself out of the normals, it is not drawn.
    float rs = max(dot(R, uSunDir), 0.0);
    // The tight lobe is the glitter itself. The broad one is only a hint of
    // sheen: at exponent 60 it covered most of the frame and added as much
    // light as the water's own colour, which is what bleached the near sea.
    float spec = pow(rs, 900.0);
    float broad = pow(rs, 400.0);
    col += uSunColor * (spec * 8.0 + broad * 0.10) * uSparkle;

    // 4. FOAM where the jacobian said the wave folded over.
    float foam = clamp(nf.z, 0.0, 1.0) * vFade;
    col = mix(col, vec3(0.92, 0.95, 0.97), foam * 0.85);

    // HAZE DRIVEN BY THE RAY'S ELEVATION, not by world distance. Distance runs
    // away towards the horizon - the last thousand units of sea occupy a couple
    // of pixels - so a distance ramp does all its work in that sliver and draws
    // the hard line it was supposed to dissolve. Elevation maps almost linearly
    // to screen height near the horizon, so this spreads the same fade over a
    // wide band and the sea melts into the sky instead of meeting it.
    // Towards the horizon COLOUR rather than a second full skyColor() call:
    // that call ran the cloud fbm a second time for every water pixel on the
    // screen and bought a difference nobody can see at that distance.
    vec3 toFrag = normalize(vWorld - uCamPos);
    float haze = smoothstep(-0.085, -0.004, toFrag.y);
    col = mix(col, uHorizonColor, haze * 0.96);

    // Added AFTER the safe-zone attenuation: the halo is light in front of the
    // scene, not part of the scene the attenuation is dimming.
    gl_FragColor = vec4(col * vSafe + coverHalo(vNdc), 1.0);
  }
`;
}

function skyFragment(octaves) {
  return `
  precision mediump float;
  uniform mat4 uInvViewProj;
  uniform vec3 uCamPos;
  varying vec2 vUv;

${skyChunk(octaves)}

  uniform vec3 uHaloColor;
  uniform float uHaloStrength;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;

  // A ring of light just OUTSIDE the cover, which is where the beat lives now.
  // uSafeHalfSize spans the whole overlay - cover plus the title and artist
  // under it - while the cover is the square at the TOP of that box, so its
  // half-size is the smaller of the box's two and its centre sits that far
  // below the box's top edge. Peaking outside the sleeve rather than behind it
  // keeps the text on a calm ground while the halo reads around the edge.
  vec3 coverHalo(vec2 ndc) {
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    float side = min(uSafeHalfSize.x, uSafeHalfSize.y);
    vec2 centre = vec2(uSafeCenter.x, uSafeCenter.y + uSafeHalfSize.y - side);
    float e = length(sp - centre) / max(side, 0.001);
    float d = (e - ${HALO_RING.toFixed(2)}) / ${HALO_WIDTH.toFixed(2)};
    return uHaloColor * (exp(-d * d) * uHaloStrength);
  }

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec4 pn = uInvViewProj * vec4(ndc, -1.0, 1.0);
    vec4 pf = uInvViewProj * vec4(ndc, 1.0, 1.0);
    vec3 dir = normalize(pf.xyz / pf.w - pn.xyz / pn.w);
    gl_FragColor = vec4(skyColor(dir) + coverHalo(ndc), 1.0);
  }
`;
}

export class OceanVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    // An unknown tier falls DOWN to 1, not up to DEFAULT_TIER: tier 0 means a
    // device that was measured as weak, and handing it the heaviest version is
    // exactly backwards. requiresTier keeps automatic rotation above 1 anyway.
    this._tier = TIER[tier] ? tier : (tier < 1 ? 1 : DEFAULT_TIER);

    const s = parseFloat(urlParam('oceansun'));
    this._forcedSun = Number.isFinite(s) ? s : null;
    const w = parseFloat(urlParam('oceanwind'));
    this._forcedWind = Number.isFinite(w) && w > 1 ? w : null;
    this._chop = urlParam('oceanchop') !== '0';
    this._heightDebug = urlParam('oceanheight') === '1';

    this._time = 0;
    this._paused = false;
    this._windSpeed = WIND_BASE;
    this._h0Wind = -1;
    this._h0Dirty = true;
    this._foamReset = 0;
    this._progress = 0;
    this._heightGain = 1;
    this._baseScale = 1;
    this._lowS = 0.5;
    this._midS = 0.5;
    this._highS = 0.5;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;
    const n = layout.n;
    this._n = n;
    this._stages = Math.round(Math.log2(n));

    // Raw transform output -> world units, computed rather than guessed. The
    // field is h(x) = sum_k H(k) e^{ikx} with no normalisation applied in the
    // butterflies, and its variance is 2 * sum_k P(k) - so summing the Phillips
    // spectrum on the CPU gives the exact rms the transform is about to
    // produce, and the scale that lands it on TARGET_RMS falls straight out.
    // Recomputed only when the wind moves (see update), which is also the only
    // time H0 is rebuilt.
    this._fieldScale = 1.0;      // replaced by _refreshScale at the end of init,
                                 // once the uniforms it writes into exist

    // RepeatWrapping is not optional here: the patch is periodic by
    // construction (that is what makes the FFT tile seamlessly), and the grid
    // samples it at world.xz/PATCH, which runs to tens of patches out. Clamped,
    // everything past the first patch collapses to one edge texel.
    const fixed = {
      width: n, height: n, type: THREE.FloatType,
      filter: THREE.NearestFilter, wrap: THREE.RepeatWrapping
    };
    this._h0Buf = new FeedbackBuffer(Object.assign({ single: true }, fixed));
    this._fftBuf = new FeedbackBuffer(Object.assign({}, fixed));
    this._normBuf = new FeedbackBuffer(Object.assign({}, fixed));

    this._sunDir = new THREE.Vector3(0, 0.5, -1).normalize();
    this._sunColor = new THREE.Color(1, 0.96, 0.9);
    this._zenith = new THREE.Color(0.09, 0.24, 0.55);
    this._horizon = new THREE.Color(0.52, 0.66, 0.80);
    this._haloColor = new THREE.Color(0x8fd8ff);
    this._deep = new THREE.Color(0.012, 0.062, 0.088);
    this._shallow = new THREE.Color(0.04, 0.17, 0.19);

    this._h0Uniforms = {
      uN: { value: n },
      uPatch: { value: PATCH_SIZE },
      uWindSpeed: { value: WIND_BASE },
      uWindDir: { value: new THREE.Vector2(1, 0.32).normalize() },
      uAmplitude: { value: AMPLITUDE_BASE }
    };
    this._specUniforms = {
      uH0: { value: null },
      uN: { value: n },
      uPatch: { value: PATCH_SIZE },
      uTime: { value: 0 },
      uChoppy: { value: this._chop ? CHOPPY : 0.0 }
    };
    this._fftUniforms = {
      uSrc: { value: null },
      uN: { value: n },
      uSubSize: { value: 2 },
      uHorizontal: { value: 1 },
      uUnshift: { value: 0 }
    };
    this._normUniforms = {
      uDisp: { value: null },
      uPrev: { value: null },
      uN: { value: n },
      uPatch: { value: PATCH_SIZE },
      uScale: { value: this._fieldScale },
      uFoamDecay: { value: FOAM_DECAY },
      uFoamThreshold: { value: FOAM_THRESHOLD },
      uReset: { value: 0 }
    };

    const mk = (frag, uniforms) => new THREE.ShaderMaterial({
      uniforms, vertexShader: QUAD_VERTEX, fragmentShader: frag,
      depthTest: false, depthWrite: false
    });
    this._h0Mat = mk(H0_FRAGMENT, this._h0Uniforms);
    this._specMat = mk(SPECTRUM_FRAGMENT, this._specUniforms);
    this._fftMat = mk(FFT_FRAGMENT, this._fftUniforms);
    this._normMat = mk(NORMFOAM_FRAGMENT, this._normUniforms);

    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._h0Mat);
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);
    this._simCam = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._simCam.position.z = 1;

    // --- sky, behind everything
    this._skyUniforms = {
      uInvViewProj: { value: new THREE.Matrix4() },
      uCamPos: { value: new THREE.Vector3() },
      uSunDir: { value: this._sunDir },
      uSunColor: { value: this._sunColor },
      uZenithColor: { value: this._zenith },
      uHorizonColor: { value: this._horizon },
      uSunElevation: { value: SUN_ELEVATION_START },
      uCloudDrift: { value: 0 },
      uHaloColor: { value: this._haloColor },
      uHaloStrength: { value: HALO_BASE },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize
    };
    this._sky = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._skyUniforms,
      vertexShader: SKY_VERTEX,
      fragmentShader: skyFragment(layout.cloudOctaves),
      depthTest: true, depthWrite: false
    }));
    this._sky.frustumCulled = false;
    // AFTER the water, not before. Drawn first it was shaded across the whole
    // frame and then painted over by the sea; drawn last, sitting on the far
    // plane, the depth test throws away every sky pixel the water covers -
    // which on this framing is most of them.
    this._sky.renderOrder = 1000;
    scene.add(this._sky);

    // --- water
    this._waterUniforms = Object.assign({
      uInvViewProj: { value: new THREE.Matrix4() },
      uCamPos: { value: new THREE.Vector3() },
      uDisp: { value: null },
      uNorm: { value: null },
      uN: { value: n },
      uPatch: { value: PATCH_SIZE },
      uScale: { value: this._fieldScale },
      uMarginX: { value: 1.14 },
      uMarginUp: { value: 1.04 },
      uMarginDown: { value: 1.55 },
      uMaxDist: { value: 3200 },
      uFadeStart: { value: 260 },
      uDeepColor: { value: this._deep },
      uShallowColor: { value: this._shallow },
      uSparkle: { value: 1.0 },
      uHeightDebug: { value: this._heightDebug ? 1 : 0 },
      uSunDir: { value: this._sunDir },
      uSunColor: { value: this._sunColor },
      uZenithColor: { value: this._zenith },
      uHorizonColor: { value: this._horizon },
      uSunElevation: { value: SUN_ELEVATION_START },
      uCloudDrift: { value: 0 },
      uHaloColor: { value: this._haloColor },
      uHaloStrength: { value: HALO_BASE }
    }, {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    const g = layout.grid;
    this._water = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2, g, g),
      new THREE.ShaderMaterial({
        uniforms: this._waterUniforms,
        vertexShader: WATER_VERTEX,
        fragmentShader: waterFragment(layout.reflectOctaves)
      })
    );
    this._water.frustumCulled = false;
    this._water.renderOrder = -1000;
    // The simulation hangs off the FIRST thing drawn, which is now the water.
    const self = this;
    this._water.onBeforeRender = function (renderer) { self._simulate(renderer); };
    scene.add(this._water);

    this._refreshScale();
    this._placeCamera();
  }

  // Sum of the Phillips spectrum over the grid: sqrt(2 * sum) is the rms the
  // inverse transform will come out with, before any scaling.
  _spectrumRms(wind) {
    const n = this._n;
    const wd = this._h0Uniforms.uWindDir.value;
    const L = wind * wind / GRAVITY;
    const k0 = 2 * Math.PI / PATCH_SIZE;
    const lmin = CUTOFF_TEXELS * PATCH_SIZE / n;
    let sum = 0;
    for (let j = 0; j < n; j++) {
      const kz = k0 * (j - n / 2);
      for (let i = 0; i < n; i++) {
        const kx = k0 * (i - n / 2);
        const kk = kx * kx + kz * kz;
        if (kk < 1e-6) continue;
        const kl = Math.sqrt(kk);
        const kh = (kx * wd.x + kz * wd.y) / kl;
        let p = AMPLITUDE_BASE * Math.exp(-1 / (kk * L * L)) / (kk * kk);
        p *= kh * kh;
        p *= Math.exp(-kk * lmin * lmin * 0.25);
        sum += p;
      }
    }
    return Math.sqrt(2 * sum);
  }

  _refreshScale() {
    const rms = this._spectrumRms(this._h0Uniforms.uWindSpeed.value);
    this._baseScale = rms > 1e-9 ? TARGET_RMS / rms : 0;
    this._fieldScale = this._baseScale * this._heightGain;
    if (this._normUniforms) this._normUniforms.uScale.value = this._fieldScale;
    if (this._waterUniforms) this._waterUniforms.uScale.value = this._fieldScale;
  }

  _placeCamera() {
    const c = this._camera;
    if (c.fov !== FOV) { c.fov = FOV; c.updateProjectionMatrix(); }
    c.position.set(0, CAMERA_HEIGHT, 0);
    // Pitch chosen so the horizon lands at 42% of the frame height: the cover
    // art then sits in the sky, above the water, with nothing to fight.
    c.lookAt(0, CAMERA_HEIGHT - CAMERA_PITCH * 40, -40);
  }

  // Everything GPU-side happens here, the one place a visualizer is allowed to
  // hold the renderer.
  _simulate(renderer) {
    // Called from the sky's onBeforeRender, which is the first thing drawn and
    // the only point where the camera matrices are final - scene-manager's
    // drift lands after update() returns.
    this._syncMatrices();
    this._h0Buf.ensureSize(renderer);
    this._fftBuf.ensureSize(renderer);
    this._normBuf.ensureSize(renderer);

    const prev = renderer.getRenderTarget();

    if (this._h0Dirty) {
      this._simQuad.material = this._h0Mat;
      renderer.setRenderTarget(this._h0Buf.read);
      renderer.render(this._simScene, this._simCam);
      this._h0Dirty = false;
    }

    if (!this._paused) {
      // spectrum -> fft.write
      this._specUniforms.uH0.value = this._h0Buf.read.texture;
      this._specUniforms.uTime.value = this._time;
      this._simQuad.material = this._specMat;
      renderer.setRenderTarget(this._fftBuf.write);
      renderer.render(this._simScene, this._simCam);
      this._fftBuf.swap();

      // 2 * log2(N) butterfly passes, one program throughout
      this._simQuad.material = this._fftMat;
      for (let axis = 0; axis < 2; axis++) {
        this._fftUniforms.uHorizontal.value = axis === 0 ? 1 : 0;
        for (let s = 0; s < this._stages; s++) {
          this._fftUniforms.uSubSize.value = Math.pow(2, s + 1);
          this._fftUniforms.uUnshift.value = (axis === 1 && s === this._stages - 1) ? 1 : 0;
          this._fftUniforms.uSrc.value = this._fftBuf.read.texture;
          renderer.setRenderTarget(this._fftBuf.write);
          renderer.render(this._simScene, this._simCam);
          this._fftBuf.swap();
        }
      }

      // normals + foam
      this._normUniforms.uDisp.value = this._fftBuf.read.texture;
      this._normUniforms.uPrev.value = this._normBuf.read.texture;
      this._normUniforms.uReset.value = this._foamReset;
      this._foamReset = 1;
      this._simQuad.material = this._normMat;
      renderer.setRenderTarget(this._normBuf.write);
      renderer.render(this._simScene, this._simCam);
      this._normBuf.swap();
    }

    renderer.setRenderTarget(prev);
    this._waterUniforms.uDisp.value = this._fftBuf.read.texture;
    this._waterUniforms.uNorm.value = this._normBuf.read.texture;
  }

  onTrackChange() {
    this._foamReset = 0;     // a new track starts on clean water
    this._time = 0;
  }

  onPlayPause(state) {
    this._paused = state !== 'PLAYING';
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const dyn = director && director.character && director.character.dynamics != null
      ? clamp01(director.character.dynamics) : 0.5;
    const swing = 0.45 + 0.55 * dyn;

    // Wind sets the dominant wavelength; amplitude sets how deep the sea runs.
    // Exponential smoothing before anything touches the water.
    const kSlow = 1 - Math.exp(-deltaTime / BAND_TAU);
    const kFast = 1 - Math.exp(-deltaTime / SPARKLE_TAU);
    this._lowS += (low - this._lowS) * kSlow;
    this._midS += (mid - this._midS) * kSlow;
    this._highS += (high - this._highS) * kFast;

    const wind = this._forcedWind != null
      ? this._forcedWind
      : WIND_BASE * (1 + (this._midS - 0.5) * 2 * WIND_BAND * swing);
    this._windSpeed = wind;
    // bands.low moves the sea's HEIGHT, on top of the normalisation - if it
    // moved the Phillips amplitude instead, the normalisation would cancel it
    // out exactly and the bass would do nothing at all.
    this._heightGain = 1 + (this._lowS - 0.5) * 2 * AMPLITUDE_BAND * swing;
    // H0 is only worth rebuilding when the wind has actually moved - the
    // gaussians are index-derived, so this morphs the sea instead of resetting.
    if (Math.abs(wind - this._h0Wind) > 0.30) {
      this._h0Uniforms.uWindSpeed.value = wind;
      this._h0Wind = wind;
      this._h0Dirty = true;
      this._refreshScale();
    } else {
      this._fieldScale = this._baseScale * this._heightGain;
      this._normUniforms.uScale.value = this._fieldScale;
      this._waterUniforms.uScale.value = this._fieldScale;
    }
    this._waterUniforms.uSparkle.value = 0.72 + 0.55 * this._highS * swing;

    // THE BEAT, on the cover's halo. director.pulse carries the shared no-BPM
    // breathing, so this moves on every track whether or not a tempo was found.
    const pulse = director && director.pulse
      ? director.pulse(HALO_PULSE, HALO_DECAY)
      : 1;
    const halo = HALO_BASE * pulse;
    this._waterUniforms.uHaloStrength.value = halo;
    this._skyUniforms.uHaloStrength.value = halo;

    // --- the sun's passage across the track
    const p = profile.duration > 0
      ? clamp01(profile.progress)
      : ((Date.now() - (profile.trackChangedAt || Date.now())) / 1000 / RADIO_LOOP_SECONDS) % 1;
    this._progress = p;
    const elev = this._forcedSun != null ? this._forcedSun : lerp(SUN_ELEVATION_START, 0.0, p);
    const ce = Math.cos(elev), se = Math.sin(elev);
    this._sunDir.set(Math.sin(SUN_AZIMUTH) * ce, se, -Math.cos(SUN_AZIMUTH) * ce).normalize();

    // High and pale, then orange, then red as it settles onto the horizon.
    const lowSun = 1 - clamp01(elev / SUN_ELEVATION_START);
    this._sunColor.setRGB(
      lerp(1.0, 1.0, lowSun),
      lerp(0.96, 0.52, lowSun * lowSun),
      lerp(0.90, 0.22, Math.pow(lowSun, 1.4))
    );
    this._zenith.setRGB(
      lerp(0.09, 0.08, lowSun), lerp(0.24, 0.13, lowSun), lerp(0.55, 0.30, lowSun));
    this._horizon.setRGB(
      lerp(0.52, 0.95, lowSun), lerp(0.66, 0.46, lowSun), lerp(0.80, 0.26, lowSun));
    this._skyUniforms.uSunElevation.value = elev;
    this._waterUniforms.uSunElevation.value = elev;
    this._skyUniforms.uCloudDrift.value = this._time * 0.004;
    this._waterUniforms.uCloudDrift.value = this._time * 0.004;

    // --- palette inflection, held well short of literal
    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors && palette.vividColors.length
      ? palette.vividColors : (palette && palette.dominantColors) || null;
    if (vivid && vivid.length) {
      const c = new THREE.Color(vivid[0]);
      const hsl = { h: 0, s: 0, l: 0 };
      c.getHSL(hsl);
      // The artwork LEANS the hue inside a blue-green window; it never gets to
      // choose it. Interpolating along the hue circle was wrong: red sits
      // almost exactly opposite blue-green, so the shortest path was a coin
      // toss and a red cover came out violet (#2d2555). Instead the cover's
      // WARMTH - how close its hue is to red - slides the result a bounded
      // distance towards the green side, which is the warm side of blue-green.
      // The result is confined to [0.45, 0.58] whatever the cover is.
      const warmth = Math.cos(hsl.h * 2 * Math.PI);
      const h = (HUE_TARGET - warmth * HUE_WINDOW + 1) % 1;
      const s = Math.min(0.85, 0.42 + hsl.s * 0.25);
      this._deep.setHSL(h, s, 0.055);
      this._shallow.setHSL(h, s * 0.82, 0.13);
      // The halo keeps the ARTWORK's own hue, unlike the water: it is light
      // coming off the sleeve, so it is the one place the cover is allowed to
      // show its real colour.
      this._haloColor.setHSL(hsl.h, Math.min(0.9, hsl.s * 0.9 + 0.25), 0.62);
    }

    this._placeCamera();
  }

  _syncMatrices() {
    const c = this._camera;
    c.updateMatrixWorld();
    const inv = new THREE.Matrix4();
    inv.multiplyMatrices(c.projectionMatrix, c.matrixWorldInverse).invert();
    this._skyUniforms.uInvViewProj.value.copy(inv);
    this._waterUniforms.uInvViewProj.value.copy(inv);
    this._skyUniforms.uCamPos.value.copy(c.position);
    this._waterUniforms.uCamPos.value.copy(c.position);
  }

  dispose() {
    this._scene.remove(this._sky);
    this._scene.remove(this._water);
    this._sky.geometry.dispose();
    this._sky.material.dispose();
    this._water.geometry.dispose();
    this._water.material.dispose();
    this._simQuad.geometry.dispose();
    this._h0Mat.dispose();
    this._specMat.dispose();
    this._fftMat.dispose();
    this._normMat.dispose();
    this._h0Buf.dispose();
    this._fftBuf.dispose();
    this._normBuf.dispose();
  }
}
